import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/DashboardLayout';
import BuyBackListingsTab from '@/pages/BuyBackListingsTab';
import BuyBackDashboardTab from '@/pages/BuyBackDashboardTab';
import {
  getBuybackOrders, getBuybackPending, getBuybackOrderMessages,
  sendBuybackMessage, validateBuybackOrder, suspendBuybackOrder,
  getBuybackSuspendReasons, getDashboardStats, getBuybackOrder,
  downloadBuybackCertificate, syncBuybackCertificates, getCertificatesStatus
} from '@/services/api';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, AlertCircle, Search, ChevronLeft, ChevronRight,
  MessageSquare, CheckCircle2, PauseCircle, Send, Clock, Truck,
  Euro, User, Phone, MapPin, ChevronDown, ChevronUp, Filter, Package,
  BarChart3, FileText, Download
} from 'lucide-react';

const STATUS_CONFIG = {
  'MONEY_TRANSFERED': { label: 'Paye', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  'RECEIVED': { label: 'Recu', color: 'bg-blue-100 text-blue-800', icon: Package },
  'SENT': { label: 'Expedie', color: 'bg-cyan-100 text-cyan-800', icon: Truck },
  'SUSPENDED': { label: 'Suspendu', color: 'bg-amber-100 text-amber-800', icon: PauseCircle },
  'CANCELED': { label: 'Annule', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  'PAID': { label: 'Valide', color: 'bg-purple-100 text-purple-800', icon: Euro },
  'COUNTER_OFFERED': { label: 'Contre-offre', color: 'bg-orange-100 text-orange-800', icon: Euro },
  'TO_SEND': { label: 'A expedier', color: 'bg-indigo-100 text-indigo-800', icon: Send },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-800', icon: Clock };
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} border-0 text-xs gap-1`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </Badge>
  );
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function BuyBackPage() {
  const [orders, setOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [suspendReasons, setSuspendReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actioning, setActioning] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const pageSize = 20;

  useEffect(() => {
    (async () => {
      try {
        const stats = await getDashboardStats();
        setConnected(stats.backmarket_connected);
      } catch { setConnected(false); }
      finally { setCheckingConnection(false); }
    })();
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    try {
      const statusParam = filterStatus !== 'all' ? filterStatus : null;
      const [ordersData, pendingData] = await Promise.all([
        getBuybackOrders(page, pageSize, statusParam),
        getBuybackPending()
      ]);
      setOrders(ordersData.results || []);
      setTotalCount(ordersData.count || 0);
      setPendingOrders(pendingData.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement BuyBack');
    } finally { setLoading(false); }
  }, [connected, page, filterStatus]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const toggleExpand = async (order) => {
    const oid = order.orderPublicId;
    if (expandedOrder === oid) {
      setExpandedOrder(null);
      return;
    }
    setExpandedOrder(oid);
    setLoadingMessages(true);
    try {
      const data = await getBuybackOrderMessages(oid);
      setMessages(data.results || []);
    } catch { setMessages([]); }
    finally { setLoadingMessages(false); }
  };

  const handleSendMessage = async (orderId) => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await sendBuybackMessage(orderId, newMessage);
      toast.success('Message envoye');
      setNewMessage('');
      const data = await getBuybackOrderMessages(orderId);
      setMessages(data.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi message');
    } finally { setSendingMessage(false); }
  };

  const handleValidate = async (orderId) => {
    setActioning(true);
    try {
      await validateBuybackOrder(orderId);
      toast.success(`Commande ${orderId} validee`);
      setActionDialog(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur validation');
    } finally { setActioning(false); }
  };

  const handleSuspend = async (orderId) => {
    if (!selectedReason) { toast.error('Selectionnez une raison'); return; }
    setActioning(true);
    try {
      await suspendBuybackOrder(orderId, selectedReason, actionMessage);
      toast.success(`Commande ${orderId} suspendue`);
      setActionDialog(null);
      setSelectedReason('');
      setActionMessage('');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suspension');
    } finally { setActioning(false); }
  };

  const openSuspendDialog = async (order) => {
    setActionDialog({ type: 'suspend', order });
    if (suspendReasons.length === 0) {
      try {
        const reasons = await getBuybackSuspendReasons();
        setSuspendReasons(reasons);
      } catch { setSuspendReasons([]); }
    }
  };

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) { setSearchResult(null); return; }
    // If it looks like an order ID (contains dash or digits), try direct lookup
    if (q.includes('-') || /^\d{5,}$/.test(q)) {
      setSearching(true);
      try {
        const order = await getBuybackOrder(q);
        if (order && order.orderPublicId) {
          setSearchResult(order);
          toast.success(`Commande ${order.orderPublicId} trouvee`);
        } else {
          setSearchResult(null);
          toast.error('Commande non trouvee');
        }
      } catch {
        setSearchResult(null);
        toast.error('Commande non trouvee');
      } finally { setSearching(false); }
    } else {
      setSearchResult(null);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResult(null);
  };

  const stats = useMemo(() => ({
    total: totalCount,
    pending: pendingOrders.length,
    received: orders.filter(o => o.status === 'RECEIVED').length,
    suspended: orders.filter(o => o.status === 'SUSPENDED').length,
  }), [totalCount, pendingOrders, orders]);

  const filteredOrders = useMemo(() => {
    if (searchResult) return orders;
    if (!searchQuery) return orders;
    const q = searchQuery.toLowerCase();
    return orders.filter(o =>
      o.listing?.title?.toLowerCase().includes(q) ||
      o.orderPublicId?.toLowerCase().includes(q) ||
      o.customer?.lastName?.toLowerCase().includes(q) ||
      o.customer?.firstName?.toLowerCase().includes(q) ||
      o.trackingNumber?.toLowerCase().includes(q)
    );
  }, [orders, searchQuery]);

  const totalPages = Math.ceil(totalCount / pageSize);

  if (checkingConnection) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div></DashboardLayout>;
  }

  if (!connected) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="buyback-page">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Rachat</h1>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-900">Back Market non connecte</h3>
                  <p className="text-sm text-amber-700 mt-1">Configurez votre token API dans les parametres.</p>
                  <Button variant="outline" className="mt-4 border-amber-300 text-amber-700" onClick={() => window.location.href = '/settings'}>Configurer</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="buyback-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Rachat</h1>
            <p className="text-slate-600 mt-1">{stats.total} commandes de rachat · {stats.pending} en attente</p>
          </div>
          {activeTab === 'orders' && (
            <Button onClick={fetchOrders} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600" data-testid="refresh-buyback-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualiser
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="buyback-tabs">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'orders' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-orders"
          >
            Commandes
          </button>
          <button
            onClick={() => setActiveTab('listings')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'listings' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-listings"
          >
            Annonces
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'dashboard' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-dashboard"
          >
            <span className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" />Tableau de bord</span>
          </button>
        </div>

        {activeTab === 'listings' ? (
          <BuyBackListingsTab />
        ) : activeTab === 'dashboard' ? (
          <BuyBackDashboardTab />
        ) : (
        <>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'bg-slate-100 text-slate-700', icon: Package },
            { label: 'En attente', value: stats.pending, color: stats.pending > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700', icon: Clock },
            { label: 'Recus', value: stats.received, color: 'bg-blue-50 text-blue-700', icon: Package },
            { label: 'Suspendus', value: stats.suspended, color: 'bg-amber-50 text-amber-700', icon: PauseCircle },
          ].map((s, i) => (
            <Card key={i} className={`border-0 ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="py-3 px-3 flex items-center gap-2">
                <s.icon className="w-4 h-4 opacity-60" />
                <div><p className="text-xl font-bold">{s.value}</p><p className="text-xs opacity-60">{s.label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending Alert */}
        {pendingOrders.length > 0 && (
          <Card className="border-red-200 bg-red-50" data-testid="pending-alert">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">{pendingOrders.length} commande(s) en attente de reponse</h3>
              </div>
              <div className="space-y-2">
                {pendingOrders.map(o => (
                  <div key={o.orderPublicId} className="flex items-center justify-between p-2 bg-white rounded-lg border border-red-100">
                    <div className="flex items-center gap-3">
                      <StatusBadge status={o.status} />
                      <span className="font-medium text-sm">{o.listing?.title}</span>
                      <span className="text-xs text-slate-500">{o.orderPublicId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{o.originalPrice?.value} {o.originalPrice?.currency}</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleExpand(o)} data-testid={`expand-pending-${o.orderPublicId}`}>
                        <MessageSquare className="w-3 h-3 mr-1" />Voir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-3 pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="N° commande (ex: FR-26132-TNGPP)" value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) setSearchResult(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="pl-10 h-9" data-testid="search-buyback-input" />
              </div>
              <Button variant="outline" size="sm" className="h-9" onClick={handleSearch} disabled={searching || !searchQuery.trim()} data-testid="search-buyback-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
              {searchResult && (
                <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={clearSearch} data-testid="clear-search-btn">
                  Effacer
                </Button>
              )}
              <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); setPage(1); setSearchResult(null); }}>
                <SelectTrigger className="w-[150px] h-9" data-testid="filter-buyback-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="TO_SEND">A expedier</SelectItem>
                  <SelectItem value="RECEIVED">Recu</SelectItem>
                  <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                  <SelectItem value="SENT">Expedie</SelectItem>
                  <SelectItem value="MONEY_TRANSFERED">Paye</SelectItem>
                  <SelectItem value="CANCELED">Annule</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Search Result */}
        {searchResult && (
          <Card className="border-emerald-200 bg-emerald-50" data-testid="search-result-card">
            <CardContent className="pt-4 pb-4">
              <h3 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                <Search className="w-4 h-4" />Resultat de recherche : {searchResult.orderPublicId}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-emerald-200 text-left">
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs">ID</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs">Produit</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs">Client</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs">Statut</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs text-right">Prix</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs">Date</th>
                      <th className="py-2 px-3 font-medium text-emerald-700 text-xs text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <OrderRow
                      order={searchResult}
                      isExpanded={expandedOrder === searchResult.orderPublicId}
                      isPending={['RECEIVED', 'SUSPENDED', 'TO_SEND'].includes(searchResult.status)}
                      messages={messages}
                      loadingMessages={loadingMessages}
                      newMessage={newMessage}
                      sendingMessage={sendingMessage}
                      onToggleExpand={() => toggleExpand(searchResult)}
                      onSendMessage={() => handleSendMessage(searchResult.orderPublicId)}
                      onNewMessageChange={setNewMessage}
                      onValidate={() => setActionDialog({ type: 'validate', order: searchResult })}
                      onSuspend={() => openSuspendDialog(searchResult)}
                    />
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Orders Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {filteredOrders.length} commande(s) {totalCount > pageSize && <span className="text-slate-400 font-normal ml-2">page {page}/{totalPages}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /><span className="ml-3 text-slate-500">Chargement...</span></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left bg-slate-50/50">
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">ID</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">Produit</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">Client</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">Statut</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs text-right">Prix</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">Date</th>
                        <th className="py-2.5 px-3 font-medium text-slate-500 text-xs text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map(order => {
                        const isExpanded = expandedOrder === order.orderPublicId;
                        const isPending = ['RECEIVED', 'SUSPENDED'].includes(order.status);
                        return (
                          <OrderRow
                            key={order.orderPublicId}
                            order={order}
                            isExpanded={isExpanded}
                            isPending={isPending}
                            messages={messages}
                            loadingMessages={loadingMessages}
                            newMessage={newMessage}
                            sendingMessage={sendingMessage}
                            onToggleExpand={() => toggleExpand(order)}
                            onSendMessage={() => handleSendMessage(order.orderPublicId)}
                            onNewMessageChange={setNewMessage}
                            onValidate={() => setActionDialog({ type: 'validate', order })}
                            onSuspend={() => openSuspendDialog(order)}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredOrders.length === 0 && !loading && (
                    <div className="text-center py-12 text-slate-500">Aucune commande trouvee</div>
                  )}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4 pb-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        </>
        )}
      </div>

      {/* Validate Dialog */}
      <Dialog open={actionDialog?.type === 'validate'} onOpenChange={open => { if (!open) setActionDialog(null); }}>
        <DialogContent data-testid="validate-dialog">
          <DialogHeader>
            <DialogTitle>Valider la commande</DialogTitle>
            <DialogDescription>
              Confirmer la validation de <strong>{actionDialog?.order?.orderPublicId}</strong> — {actionDialog?.order?.listing?.title} ?
              <br />Prix: <strong>{actionDialog?.order?.originalPrice?.value} {actionDialog?.order?.originalPrice?.currency}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Annuler</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => handleValidate(actionDialog.order.orderPublicId)} disabled={actioning} data-testid="confirm-validate-btn">
              {actioning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={actionDialog?.type === 'suspend'} onOpenChange={open => { if (!open) setActionDialog(null); }}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>Suspendre la commande</DialogTitle>
            <DialogDescription>
              Suspendre <strong>{actionDialog?.order?.orderPublicId}</strong> — {actionDialog?.order?.listing?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Raison</Label>
              <Select value={selectedReason} onValueChange={setSelectedReason}>
                <SelectTrigger data-testid="suspend-reason-select"><SelectValue placeholder="Selectionnez une raison" /></SelectTrigger>
                <SelectContent>
                  {suspendReasons.map(r => (
                    <SelectItem key={r.identifier} value={r.identifier}>{r.message}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Message (optionnel)</Label>
              <Textarea value={actionMessage} onChange={e => setActionMessage(e.target.value)} placeholder="Message au client..." data-testid="suspend-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Annuler</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => handleSuspend(actionDialog.order.orderPublicId)} disabled={actioning} data-testid="confirm-suspend-btn">
              {actioning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PauseCircle className="w-4 h-4 mr-2" />}Suspendre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function OrderRow({ order, isExpanded, isPending, messages, loadingMessages, newMessage, sendingMessage, onToggleExpand, onSendMessage, onNewMessageChange, onValidate, onSuspend }) {
  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer ${isPending ? 'bg-amber-50/30' : ''}`}
          onClick={onToggleExpand} data-testid={`buyback-row-${order.orderPublicId}`}>
        <td className="py-2.5 px-3">
          <span className="font-mono text-xs text-slate-600 font-medium">{order.orderPublicId}</span>
        </td>
        <td className="py-2.5 px-3">
          <p className="font-medium text-slate-800 text-xs line-clamp-1 max-w-[200px]">{order.listing?.title}</p>
          <span className="text-[10px] text-slate-400">{order.listing?.grade}</span>
        </td>
        <td className="py-2.5 px-3">
          <span className="text-xs">{order.customer?.firstName} {order.customer?.lastName}</span>
        </td>
        <td className="py-2.5 px-3"><StatusBadge status={order.status} /></td>
        <td className="py-2.5 px-3 text-right">
          <span className="font-semibold text-sm">{order.originalPrice?.value} {order.originalPrice?.currency}</span>
          {order.counterOfferPrice && order.counterOfferPrice.value !== order.originalPrice?.value && (
            <p className="text-[10px] text-amber-600">Contre-offre: {order.counterOfferPrice.value} {order.counterOfferPrice.currency}</p>
          )}
        </td>
        <td className="py-2.5 px-3">
          <span className="text-xs text-slate-500">{formatDate(order.creationDate)}</span>
        </td>
        <td className="py-2.5 px-3 text-center">
          <div className="flex items-center justify-center gap-1">
            {isPending && order.status === 'RECEIVED' && (
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-emerald-600" onClick={e => { e.stopPropagation(); onValidate(); }}
                title="Valider" data-testid={`validate-btn-${order.orderPublicId}`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
              </Button>
            )}
            {isPending && (
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-amber-600" onClick={e => { e.stopPropagation(); onSuspend(); }}
                title="Suspendre" data-testid={`suspend-btn-${order.orderPublicId}`}>
                <PauseCircle className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400" onClick={e => { e.stopPropagation(); onToggleExpand(); }}
              data-testid={`expand-btn-${order.orderPublicId}`}>
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr data-testid={`details-${order.orderPublicId}`}>
          <td colSpan={7} className="p-0">
            <div className="bg-slate-50 border-b border-slate-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Client Info */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1"><User className="w-3 h-3" />Client</h4>
                  <p className="text-sm font-medium">{order.customer?.firstName} {order.customer?.lastName}</p>
                  {order.customer?.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{order.customer.phone}</p>}
                  {order.returnAddress && (
                    <p className="text-xs text-slate-500 flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {order.returnAddress.address1}, {order.returnAddress.zipcode} {order.returnAddress.city}
                    </p>
                  )}
                </div>
                {/* Order Info */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1"><Package className="w-3 h-3" />Details</h4>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-slate-500">Creation:</span><span>{formatDate(order.creationDate)}</span>
                    <span className="text-slate-500">Expedition:</span><span>{formatDate(order.shippingDate)}</span>
                    <span className="text-slate-500">Reception:</span><span>{formatDate(order.receivalDate)}</span>
                    <span className="text-slate-500">Paiement:</span><span>{formatDate(order.paymentDate)}</span>
                    {order.trackingNumber && <><span className="text-slate-500">Tracking:</span><span className="font-mono">{order.trackingNumber}</span></>}
                    {order.shipper && <><span className="text-slate-500">Transporteur:</span><span>{order.shipper}</span></>}
                  </div>
                  {order.transferCertificateLink && (
                    <CertificateButton orderId={order.orderPublicId} />
                  )}
                </div>
                {/* Pricing */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1"><Euro className="w-3 h-3" />Prix</h4>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-slate-500">Prix original:</span>
                    <span className="font-semibold">{order.originalPrice?.value} {order.originalPrice?.currency}</span>
                    {order.counterOfferPrice && order.counterOfferPrice.value !== order.originalPrice?.value && (
                      <>
                        <span className="text-slate-500">Contre-offre:</span>
                        <span className="font-semibold text-amber-600">{order.counterOfferPrice.value} {order.counterOfferPrice.currency}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="border-t border-slate-200 pt-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <MessageSquare className="w-3 h-3" />Messages
                </h4>
                {loadingMessages ? (
                  <div className="flex items-center gap-2 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs text-slate-500">Chargement...</span></div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {messages.length === 0 && <p className="text-xs text-slate-400 py-2">Aucun message</p>}
                    {messages.map((msg, i) => (
                      <div key={i} className={`p-2 rounded-lg text-xs ${msg.author === 'Client' ? 'bg-white border border-slate-200 ml-0 mr-8' : 'bg-emerald-50 border border-emerald-200 ml-8 mr-0'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-semibold text-[10px] ${msg.author === 'Client' ? 'text-slate-600' : 'text-emerald-700'}`}>{msg.author}</span>
                          <span className="text-[10px] text-slate-400">{formatDateTime(msg.date)}</span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: msg.body }} />
                      </div>
                    ))}
                  </div>
                )}
                {/* Send message */}
                {isPending && (
                  <div className="flex gap-2 mt-3">
                    <Input value={newMessage} onChange={e => onNewMessageChange(e.target.value)} placeholder="Votre message..."
                      className="h-8 text-xs" onKeyDown={e => e.key === 'Enter' && !e.shiftKey && onSendMessage()}
                      data-testid={`message-input-${order.orderPublicId}`} />
                    <Button size="sm" className="h-8 bg-emerald-500 hover:bg-emerald-600" onClick={onSendMessage} disabled={sendingMessage || !newMessage.trim()}
                      data-testid={`send-message-btn-${order.orderPublicId}`}>
                      {sendingMessage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function CertificateButton({ orderId }) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const blob = await downloadBuybackCertificate(orderId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_cession_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bon de cession telecharge');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur telechargement');
    } finally { setLoading(false); }
  };

  return (
    <Button size="sm" variant="outline" className="mt-2 h-7 text-xs gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
      onClick={handleDownload} disabled={loading} data-testid={`cert-btn-${orderId}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
      Bon de cession
    </Button>
  );
}
